import React from 'react';
import Link from 'next/link';
import SitePhoto from '@/components/ui/SitePhoto';
import type { Visual } from '@/data/journey';

/**
 * "Next visual" prompts — the site's way of leading a visitor from what they are looking at to the
 * next appealing thing (a project, its 3D vs actual, a before/after, an idea). Plain links with
 * descriptive text and real images in the server HTML, so they help search engines as much as people.
 */

export interface NextItem {
  kicker: string; // small label, e.g. "3D VS ACTUAL"
  title: string; // the prompt, written as the next thought: "See how close the 3D was to the finished home"
  text?: string;
  href: string;
  image?: Visual;
  /** Two images side by side, e.g. a 3D render and the finished photo */
  pair?: { first: Visual; second: Visual; firstLabel: string; secondLabel: string };
}

function Tag({ children }: { children: React.ReactNode }) {
  return (
    <span
      className="absolute left-2 top-2 px-2 py-0.5 text-[0.6rem] font-semibold uppercase tracking-[0.12em]"
      style={{ backgroundColor: 'rgba(23,22,20,0.72)', color: '#FAF8F5' }}
    >
      {children}
    </span>
  );
}

export function NextCard({ item, sizes = '(min-width: 1024px) 30vw, 100vw' }: { item: NextItem; sizes?: string }) {
  return (
    <Link href={item.href} className="group flex flex-col h-full">
      {item.pair ? (
        <div className="grid grid-cols-2 gap-1.5 items-start">
          {[
            [item.pair.first, item.pair.firstLabel],
            [item.pair.second, item.pair.secondLabel],
          ].map(([v, label]) => (
            <div key={(v as Visual).src} className="relative hover-zoom overflow-hidden">
              <SitePhoto photo={v as Visual} aspectRatio={`${(v as Visual).width}/${(v as Visual).height}`} sizes="(min-width: 1024px) 15vw, 50vw" />
              <Tag>{label as string}</Tag>
            </div>
          ))}
        </div>
      ) : item.image ? (
        <div className="hover-zoom overflow-hidden">
          <SitePhoto photo={item.image} aspectRatio={`${item.image.width}/${item.image.height}`} sizes={sizes} />
        </div>
      ) : null}
      <span className="mt-3 text-section-label">{item.kicker}</span>
      <span className="mt-1.5 text-base md:text-lg font-semibold leading-snug text-foreground group-hover:text-primary">
        {item.title} →
      </span>
      {item.text && <span className="mt-1 text-sm leading-relaxed" style={{ color: 'var(--secondary)' }}>{item.text}</span>}
    </Link>
  );
}

/** A row of 1–3 next-visual cards with a short lead-in line (not a heading, so page outlines stay clean) */
export function NextVisuals({
  lead,
  items,
  className = '',
  tone = 'light',
}: {
  lead: string;
  items: NextItem[];
  className?: string;
  tone?: 'light' | 'stone';
}) {
  return (
    <nav
      aria-label={lead}
      className={`${tone === 'stone' ? 'bg-stone border-y border-border' : ''} ${className}`}
      data-print-hide
    >
      <div className="container-willow py-12 md:py-16">
        <p className="text-section-label mb-6">{lead}</p>
        <ul
          className={`grid gap-6 md:gap-8 list-none m-0 p-0 items-start ${
            items.length >= 3 ? 'sm:grid-cols-2 lg:grid-cols-3' : items.length === 2 ? 'sm:grid-cols-2' : 'max-w-xl'
          }`}
        >
          {items.map((it) => (
            <li key={it.href + it.title}>
              <NextCard item={it} />
            </li>
          ))}
        </ul>
      </div>
    </nav>
  );
}

/** A one-line prompt under or beside a photo: "Nikhil Kulkarni's Gubbi Gudu villa — see the full project →" */
export function PhotoPrompt({ href, children, className = '' }: { href: string; children: React.ReactNode; className?: string }) {
  return (
    <Link href={href} className={`inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline ${className}`}>
      {children} <span aria-hidden="true">→</span>
    </Link>
  );
}
